/**
 * ChannelFilterBar — Media-Tab Filter-Leiste (M1)
 *
 * Ersetzt die ChannelPillsSelector-Einzelauswahl im Produktbilder-Sub-Tab:
 * Alle Channels werden untereinander gerendert; diese Leiste schränkt die
 * sichtbaren Channel-Sektionen ein. "Alle Kanäle" = alles sichtbar.
 *
 * a11y: role="group" + aria-pressed-Toggles (NICHT tablist/tab — es gibt kein
 * Tabpanel; jeder Chip ist ein Toggle-Button).
 */

import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { EID } from '@/lib/element-ids.registry';

export interface ChannelFilterItem {
  /** Channel-Key (= sortedChannelKeys-Eintrag, z.B. "global" oder UUID) */
  key: string;
  name: string;
  count: number;
}

interface ChannelFilterBarProps {
  channels: ChannelFilterItem[];
  /** 'all' oder ein Channel-Key */
  activeFilter: string;
  onChange: (filter: string) => void;
  /** Gesamtzahl Bilder über alle Channels (für den "Alle Kanäle"-Chip) */
  totalCount: number;
  className?: string;
}

export function ChannelFilterBar({
  channels,
  activeFilter,
  onChange,
  totalCount,
  className,
}: ChannelFilterBarProps) {
  const { t } = useTranslation();

  return (
    <div
      role="group"
      id={EID['product-images-channel-filter']}
      aria-label={t('productImages.filter.label')}
      className={cn('flex flex-wrap items-center gap-2', className)}
    >
      <span className="text-xs font-medium uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mr-1">
        {t('productImages.filter.label')}
      </span>

      <FilterChip
        id={EID['product-images-filter-all-btn']}
        label={t('productImages.filter.allChannels')}
        count={totalCount}
        isActive={activeFilter === 'all'}
        onClick={() => onChange('all')}
      />

      {channels.map((ch) => (
        <FilterChip
          key={ch.key}
          id={`product-images-filter-${ch.key}`}
          label={ch.name}
          count={ch.count}
          isActive={activeFilter === ch.key}
          onClick={() => onChange(ch.key)}
        />
      ))}
    </div>
  );
}

function FilterChip({
  id,
  label,
  count,
  isActive,
  onClick,
}: {
  id: string;
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      id={id}
      aria-pressed={isActive}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-primary-500',
        isActive
          ? 'bg-primary-500 text-white font-semibold'
          : 'bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800'
      )}
    >
      <span>{label}</span>
      <span
        className={cn(
          'inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-medium',
          isActive
            ? 'bg-white text-primary-600'
            : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400'
        )}
      >
        {count}
      </span>
    </button>
  );
}
